import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Users,
  UserCheck,
  Clock,
  Mail,
  Phone,
  MapPin,
  Loader,
  Send,
  TrendingUp,
  Calendar,
  Search,
  UserPlus,
  X,
  UserMinus,
  Trash
} from 'lucide-react';
import { showToast } from '../../../utils/toasts';
import { departmentAPI, memberAPI } from '../../../services/api';
import PermissionGuard from '../../../components/guards/PermissionGuard';
import LucideIconRenderer from '../../../components/ui/LucideIconRenderer';

interface Member {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  photo: string;
  membershipType: string;
  membershipStatus: string;
  branch: {
    name: string;
  };
  departments: string[];
}

const DepartmentDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Convert text to sentence case (first letter uppercase, rest lowercase)
  const toSentenceCase = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const [department, setDepartment] = useState<any>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);

  // ✅ Member List Search
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Add Members Modal
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [availableMembers, setAvailableMembers] = useState<Member[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [addingMembers, setAddingMembers] = useState(false);

  // ✅ Modal Search
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [modalSearching, setModalSearching] = useState(false);
  const modalSearchTimeoutRef = useRef<NodeJS.Timeout>(null);

  // Remove Member Confirmation
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);
  const [removingMember, setRemovingMember] = useState(false);

  useEffect(() => {
    fetchDepartmentDetails();
    fetchDepartmentStatistics();
  }, [id]);

  // ✅ Fetch members when page or search changes
  useEffect(() => {
    fetchDepartmentMembers();
  }, [id, currentPage]);

  // ✅ Debounced search for member list
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm) {
      searchTimeoutRef.current = setTimeout(() => {
        setCurrentPage(1); // Reset to page 1 on new search
        fetchDepartmentMembers();
      }, 300);
    } else {
      fetchDepartmentMembers();
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // ✅ Debounced search for modal
  useEffect(() => {
    if (!showAddMembersModal) return;

    if (modalSearchTimeoutRef.current) {
      clearTimeout(modalSearchTimeoutRef.current);
    }

    modalSearchTimeoutRef.current = setTimeout(() => {
      fetchAvailableMembers();
    }, 300);

    return () => {
      if (modalSearchTimeoutRef.current) {
        clearTimeout(modalSearchTimeoutRef.current);
      }
    };
  }, [memberSearchTerm, showAddMembersModal]);

  const fetchDepartmentDetails = async () => {
    try {
      setLoading(true);
      const response = await departmentAPI.getDepartment(id);

      if (response?.data.success) {
        setDepartment(response.data.data.department);
      }
    } catch (error: any) {
      showToast.error('Failed to load department details');
      navigate('/departments');
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIXED: API search for department members
  const fetchDepartmentMembers = async () => {
    try {
      setMembersLoading(true);
      const response = await departmentAPI.getDepartmentMembers(id, {
        page: currentPage,
        limit: 20,
        search: searchTerm || undefined
      });

      if (response?.data.success) {
        setMembers(response.data.data.members);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (error: any) {
      showToast.error('Failed to load members');
    } finally {
      setMembersLoading(false);
    }
  };

  const fetchDepartmentStatistics = async () => {
    try {
      const response = await departmentAPI.getDepartmentStatistics(id);

      if (response.data.success) {
        setStatistics(response.data.data.statistics);
      }
    } catch (error: any) {
      console.error('Failed to load statistics:', error);
    }
  };

  // ✅ FIXED: Fetch available members with API search + filter out current department members
  const fetchAvailableMembers = async () => {
    try {
      setModalSearching(true);

      const response = await memberAPI.getMembers({
        status: 'active',
        limit: 50,
        search: memberSearchTerm || undefined
      });

      if (response.data.success) {
        // ✅ Filter out members already in this department
        const currentMemberIds = members.map(m => m._id);
        const available = response.data.data.members.filter(
          (m: Member) => !currentMemberIds.includes(m._id) && !m.departments?.includes(id!)
        );
        setAvailableMembers(available);
      }
    } catch (error: any) {
      showToast.error('Failed to load available members');
    } finally {
      setModalSearching(false);
    }
  };

  // ✅ Add members to department
  const handleAddMembers = async () => {
    if (selectedMemberIds.length === 0) {
      showToast.error('Please select at least one member');
      return;
    }

    try {
      setAddingMembers(true);

      // Add each selected member to the department
      const promises = selectedMemberIds.map(memberId => {
        const member = availableMembers.find(m => m._id === memberId);
        const currentDepartments = member?.departments || [];

        return memberAPI.updateMember(memberId, {
          departments: [...currentDepartments, id]
        });
      });

      await Promise.all(promises);

      showToast.success(`${selectedMemberIds.length} member(s) added successfully`);
      setShowAddMembersModal(false);
      setSelectedMemberIds([]);
      setMemberSearchTerm('');
      fetchDepartmentMembers(); // Refresh the members list
      fetchDepartmentStatistics(); // Refresh stats

    } catch (error: any) {
      showToast.error(error.response?.data?.message || 'Failed to add members');
    } finally {
      setAddingMembers(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;

    try {
      setRemovingMember(true);

      // ✅ FIXED: Remove department from member's departments array
      // departments array contains ObjectIds (strings), so compare directly
      const updatedDepartments = memberToRemove.departments.filter(
        (deptId: any) => {
          // Handle both string IDs and object IDs
          const deptIdString = typeof deptId === 'string' ? deptId : deptId?._id || deptId?.id;
          return deptIdString !== id;
        }
      );

      await memberAPI.updateMember(memberToRemove._id, {
        departments: updatedDepartments
      });

      showToast.success('Member removed from department');
      setMemberToRemove(null);
      fetchDepartmentMembers(); // Refresh the members list
      fetchDepartmentStatistics(); // Refresh stats

    } catch (error: any) {
      showToast.error(error.response?.data?.message || 'Failed to remove member');
    } finally {
      setRemovingMember(false);
    }
  };

  const toggleMemberSelection = (memberId: string) => {
    setSelectedMemberIds(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!department) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          <button
            onClick={() => navigate('/departments')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>

          <div className="flex items-start space-x-4">
            <div
              className="w-16 h-16 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${department.color}20` }}
            >
              <LucideIconRenderer iconName={department.icon} className="w-8 h-8" style={{ color: department.color }} />
            </div>

            <div>
              <div className="flex flex-col items-start space-y-2">
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {toSentenceCase(department.name)}
                </h1>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  department.isActive
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {department.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              {department.description && (
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                  {department.description}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="hidden md:flex items-center space-x-3">
          <PermissionGuard permission="departments.canAddMembers">
          <button
            onClick={() => {
              setMemberSearchTerm('');
              setShowAddMembersModal(true);
              fetchAvailableMembers();
            }}
            className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Members
          </button>
          </PermissionGuard>
          <PermissionGuard permission="departments.edit">
          <button
            onClick={() => navigate(`/departments/${id}/edit`)}
            className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </button>
          </PermissionGuard>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-900/10 rounded-xl border border-blue-200 dark:border-blue-800/30 p-4 sm:p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400 mb-2">Total Members</p>
                <p className="text-2xl sm:text-4xl font-bold text-blue-900 dark:text-blue-200">
                  {statistics.totalMembers}
                </p>
              </div>
              <div className="w-10 h-10 sm:w-14 sm:h-14 bg-blue-100 dark:bg-blue-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 sm:w-7 sm:h-7 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-900/10 rounded-xl border border-green-200 dark:border-green-800/30 p-4 sm:p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-green-600 dark:text-green-400 mb-2">Active Members</p>
                <p className="text-2xl sm:text-4xl font-bold text-green-900 dark:text-green-200">
                  {statistics.activeMembers}
                </p>
              </div>
              <div className="w-10 h-10 sm:w-14 sm:h-14 bg-green-100 dark:bg-green-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
                <UserCheck className="w-5 h-5 sm:w-7 sm:h-7 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-900/20 dark:to-purple-900/10 rounded-xl border border-purple-200 dark:border-purple-800/30 p-4 sm:p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-purple-600 dark:text-purple-400 mb-2">Gender Distribution</p>
                <p className="text-lg sm:text-2xl font-bold text-purple-900 dark:text-purple-200">
                  <span className="text-xl sm:text-3xl">{statistics.maleMembers}</span>
                  <span className="text-gray-500 dark:text-gray-400 mx-2">/</span>
                  <span className="text-xl sm:text-3xl">{statistics.femaleMembers}</span>
                </p>
              </div>
              <div className="w-10 h-10 sm:w-14 sm:h-14 bg-purple-100 dark:bg-purple-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 sm:w-7 sm:h-7 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-900/20 dark:to-orange-900/10 rounded-xl border border-orange-200 dark:border-orange-800/30 p-4 sm:p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-orange-600 dark:text-orange-400 mb-2">Recent Joins</p>
                <p className="text-2xl sm:text-4xl font-bold text-orange-900 dark:text-orange-200">
                  {statistics.recentJoins}
                </p>
                <p className="text-xs text-orange-600 dark:text-orange-400 mt-2 font-medium">Last 30 days</p>
              </div>
              <div className="w-10 h-10 sm:w-14 sm:h-14 bg-orange-100 dark:bg-orange-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 sm:w-7 sm:h-7 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Members Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Members ({statistics?.totalMembers || 0})
            </h2>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search members by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {membersLoading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Loader className="w-4 h-4 text-primary-500 animate-spin" />
              </div>
            )}
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          {membersLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400">
                {searchTerm ? 'No members found' : 'No members in this department yet'}
              </p>
            </div>
          ) : (
            <>
              {/* Mobile Card List */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {members.map((member) => (
                  <div key={member._id} className="p-4">
                    <div className="flex items-start gap-3">
                      <div onClick={() => navigate(`/members/${member._id}`)} className="flex items-start gap-3 flex-1 min-w-0 cursor-pointer">
                        {member.photo ? (
                          <img
                            src={member.photo}
                            alt={`${member.firstName} ${member.lastName}`}
                            className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-11 h-11 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-primary-600 dark:text-primary-400 font-semibold text-sm">
                              {member.firstName[0]}{member.lastName[0]}
                            </span>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {member.firstName} {member.lastName}
                            </p>
                            <span className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                              member.membershipStatus === 'active'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                : member.membershipStatus === 'inactive'
                                ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                            }`}>
                              {member.membershipStatus}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{member.email}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {member.phone || '—'} · {member.branch?.name || '—'}
                          </p>
                        </div>
                      </div>
                      <PermissionGuard permission="departments.canRemoveMembers">
                        <button
                          onClick={() => setMemberToRemove(member)}
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0"
                          title="Remove from department"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </PermissionGuard>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table */}
              <table className="w-full hidden md:table">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Phone</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Branch</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-300">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {members.map((member) => (
                  <tr key={member._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    {/* Name */}
                    <td className="px-6 py-4">
                      <div
                        className="flex items-center space-x-3 cursor-pointer"
                        onClick={() => navigate(`/members/${member._id}`)}
                      >
                        {member.photo ? (
                          <img
                            src={member.photo}
                            alt={`${member.firstName} ${member.lastName}`}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-primary-600 dark:text-primary-400 font-semibold">
                              {member.firstName[0]}{member.lastName[0]}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {member.firstName} {member.lastName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {member.email}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Phone */}
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {member.phone || '—'}
                      </p>
                    </td>

                    {/* Branch */}
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {member.branch?.name || '—'}
                      </p>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        member.membershipStatus === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                          : member.membershipStatus === 'inactive'
                          ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                      }`}>
                        {member.membershipStatus}
                      </span>
                    </td>

                    {/* Action */}
                    <td className="px-6 py-4 text-right">
                      <PermissionGuard permission="departments.canRemoveMembers">
                        <button
                          onClick={() => setMemberToRemove(member)}
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Remove from department"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </PermissionGuard>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>

            <span className="text-sm text-gray-700 dark:text-gray-300">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Mobile: Floating Add Members Button */}
      <PermissionGuard permission="departments.canAddMembers">
        <button
          onClick={() => {
            setMemberSearchTerm('');
            setShowAddMembersModal(true);
            fetchAvailableMembers();
          }}
          className="md:hidden fixed right-4 z-30 w-14 h-14 rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-lg flex items-center justify-center transition-colors"
          style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
          title="Add Members"
        >
          <UserPlus className="w-6 h-6" />
        </button>
      </PermissionGuard>

      {/* ✅ Add Members Modal with API Search */}
      {showAddMembersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Add Members to {department?.name}
                </h3>
                <button
                  onClick={() => {
                    setShowAddMembersModal(false);
                    setSelectedMemberIds([]);
                    setMemberSearchTerm('');
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Search */}
              <div className="mt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search members by name, email, or phone..."
                    value={memberSearchTerm}
                    onChange={(e) => setMemberSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                  {modalSearching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader className="w-4 h-4 text-primary-500 animate-spin" />
                    </div>
                  )}
                </div>
              </div>

              {/* Selected count */}
              {selectedMemberIds.length > 0 && (
                <div className="mt-3 text-sm text-primary-600 dark:text-primary-400">
                  {selectedMemberIds.length} member(s) selected
                </div>
              )}
            </div>

            {/* Modal Body - Members List */}
            <div className="flex-1 overflow-y-auto p-6">
              {modalSearching ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="w-8 h-8 animate-spin text-primary-600" />
                </div>
              ) : availableMembers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    {memberSearchTerm
                      ? 'No members found matching your search'
                      : 'All active members are already in this department'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {availableMembers.map((member) => (
                    <label
                      key={member._id}
                      className="flex items-center p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMemberIds.includes(member._id)}
                        onChange={() => toggleMemberSelection(member._id)}
                        className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                      />
                      <div className="ml-3 flex items-center flex-1">
                        {member.photo ? (
                          <img
                            src={member.photo}
                            alt={`${member.firstName} ${member.lastName}`}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary-600 dark:bg-gray-600 flex items-center justify-center">
                            <span className="text-white dark:text-gray-300 font-semibold">
                              {member.firstName[0]}{member.lastName[0]}
                            </span>
                          </div>
                        )}
                        <div className="ml-3">
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {member.firstName} {member.lastName}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {member.email || member.phone}
                          </p>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddMembersModal(false);
                    setSelectedMemberIds([]);
                    setMemberSearchTerm('');
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  disabled={addingMembers}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMembers}
                  disabled={selectedMemberIds.length === 0 || addingMembers}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                >
                  {addingMembers ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add {selectedMemberIds.length > 0 ? `(${selectedMemberIds.length})` : 'Members'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Remove Member Confirmation Modal */}
      {memberToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Remove Member from Department?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to remove <strong>{memberToRemove.firstName} {memberToRemove.lastName}</strong> from {department.name}?
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setMemberToRemove(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                disabled={removingMember}
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveMember}
                disabled={removingMember}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 inline-flex items-center"
              >
                {removingMember ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Removing...
                  </>
                ) : (
                  <>
                    Remove
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentDetails;